import React from 'react';
import { Formik } from 'formik';

import { COURSE_STRUCTURES } from 'constants/common';

import { getTranslation } from 'helpers/getTranslation';

import { Loading } from 'components/shared/Loading';

import { MainLayout } from 'components/Layout';
import { BreadcrumbsToolbar } from 'components/Toolbar';
import { AutoSave } from 'components/AutoSave';
import { ToolbarActions } from 'components/ToolbarActions';
import { ReadOnlySection } from 'components/ReadOnlySection';

import { MainSettings } from './components/MainSettings';
import { LessonsSettings } from './components/LessonsSettings';
import { ModulesSettings } from './components/ModulesSettings';
import { StructureSelect } from './components/StructureSelect';
import { CourseResultPreview } from './components/CourseResultPreview';

import {
  convertDataToForm,
  getContextItems,
  getReadonlyCourseText,
} from './CoursePage.helpers';

import { moreContextItems } from './CoursePage.config';

import styles from './CoursePage.module.scss';

const CoursePage = ({
  loading,
  isRootAdmin,
  saveLoading,
  autoSaveLoading,
  readOnly,
  course,
  lastActionAt,
  onAutoSaveSubmit,
  onSaveClick,
  onPreviewClick,
  onStatusContextItemClick,
  onMoreContextItemClick,
}: {
  loading: any;
  isRootAdmin: any;
  saveLoading: any;
  autoSaveLoading: any;
  readOnly: any;
  course: any;
  lastActionAt: any;
  onAutoSaveSubmit: any;
  onSaveClick: any;
  onPreviewClick: any;
  onStatusContextItemClick: any;
  onMoreContextItemClick: any;
}) => (
  <Formik
    enableReinitialize
    validateOnMount
    initialValues={convertDataToForm(course)}
    onSubmit={onAutoSaveSubmit}
  >
    {({ values, setFieldValue }: { values: any; setFieldValue: any }) =>
      loading ? (
        <Loading />
      ) : (
        <MainLayout
          toolbarComponent={
            <BreadcrumbsToolbar
              breadcrumbsData={{
                courseTitle:
                  values.title || getTranslation('common.text.untitled'),
              }}
              endAdornment={
                <ToolbarActions
                  saveLoading={saveLoading}
                  isRootAdmin={isRootAdmin}
                  autoSaveLoading={autoSaveLoading}
                  contextItems={getContextItems(course.status)}
                  moreContextItems={moreContextItems}
                  updatedAt={lastActionAt}
                  status={course.status}
                  onSaveClick={() => onSaveClick(values)}
                  onPreviewClick={onPreviewClick}
                  onStatusContextItemClick={(item: any) =>
                    onStatusContextItemClick(item, { values, setFieldValue })
                  }
                  onMoreContextItemClick={onMoreContextItemClick}
                />
              }
            />
          }
        >
          <div className={styles.page}>
            <AutoSave debounceMs={1000} />
            {readOnly && (
              <ReadOnlySection text={getReadonlyCourseText(course.status)} />
            )}
            <MainSettings type={course.type} readOnly={readOnly} />
            <CourseResultPreview type={course.type} />
            {!(values as Record<string, unknown>).structure && !readOnly && (
              <StructureSelect />
            )}
            {(values as Record<string, unknown>).structure ===
              COURSE_STRUCTURES.MODULES && <ModulesSettings />}
            {(values as Record<string, unknown>).structure ===
              COURSE_STRUCTURES.LESSONS && <LessonsSettings />}
          </div>
        </MainLayout>
      )
    }
  </Formik>
);

export default React.memo(CoursePage) as any;
